import moment from 'moment';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  activateTriggerCreator,
  completeTriggerCreator,
} from 'creators/triggers';
import { selectCurrentUserCreatedAt, selectUserIsLogged } from 'selectors/user';
import { getLocalStorageKey, setLocalStorageKey } from 'utilities/localStorage';
import { getParam } from 'utilities/url';

import { useShouldShowTrigger } from 'hooks/triggers';

export default function useFtux({
  triggerKey,
  launchDate,
  maxDuration,
}: {
  triggerKey: string;
  launchDate: string | moment.Moment;
  maxDuration: { [key: string]: number } | moment.Duration;
}): [boolean, () => void] {
  const dispatch = useDispatch();
  const [, setFtuxClosed] = useState(false);
  const isLoggedIn = useSelector(selectUserIsLogged);
  let userCreatedAt = useSelector<undefined>(selectCurrentUserCreatedAt);
  const showFtuxLoggedIn = useShouldShowTrigger(triggerKey);
  const lsKey = `triggers:${triggerKey}`;

  useEffect(() => {
    // Allows us to see the ftux at any time
    const override = getParam('ftuxOverride');

    if (override === triggerKey) {
      dispatch(activateTriggerCreator(triggerKey));
      setLocalStorageKey(lsKey, {
        complete: false,
        show: true,
      });
      window.history.pushState(
        'override',
        '',
        window.location.toString().replace(window.location.search, '')
      );
    }
  }, [dispatch, triggerKey, lsKey]);

  let ftuxEnabled = false;
  let _maxDuration;

  if (launchDate) {
    launchDate = moment(launchDate);
    // @ts-expect-error ignore due to enabling strict null checks
    userCreatedAt = moment(userCreatedAt);
  }

  if (maxDuration) {
    _maxDuration = moment.duration(maxDuration);
  }

  // @ts-expect-error ignore due to enabling strict null checks
  if (launchDate && userCreatedAt && userCreatedAt > launchDate) {
    ftuxEnabled = false;
  } else if (
    launchDate &&
    _maxDuration &&
    moment().diff(launchDate, 'seconds') > _maxDuration.asSeconds()
  ) {
    ftuxEnabled = false;
    console.log(`FTUX ${triggerKey} past max duration. Consider removing.`);
  } else if (isLoggedIn) {
    ftuxEnabled = showFtuxLoggedIn;
  } else {
    const showFtux = getLocalStorageKey(lsKey);

    if (typeof showFtux === 'undefined' || showFtux?.show) {
      ftuxEnabled = true;
    } else if (!showFtux) {
      setLocalStorageKey(lsKey, {
        complete: false,
        show: true,
      });

      ftuxEnabled = true;
    }
  }

  const endFtux = () => {
    if (isLoggedIn) {
      dispatch(completeTriggerCreator(triggerKey));
    } else {
      setLocalStorageKey(lsKey, {
        complete: true,
        show: false,
      });
    }

    setFtuxClosed(true);
  };

  return [ftuxEnabled, endFtux];
}
